// The load entry (1.15.0, ADR-0065) — say what is on you.
//
// Mounted beside the bother entry and built to the same shape. Both were a
// collapsed line under capture until 2.8.1 (ADR-0099) made them surfaces
// reached from Contents; both cost nothing until you open them, which is the
// property that mattered and the one that survived the move. The two are
// deliberately separate boxes. A bother is a WORRY, pre-triage, and the flow
// exists to route it. A pebble is WEIGHT, and there is nothing to do about it —
// saying so is the entire act, and the app's response is to ask less of you,
// not more.
//
// Nothing here can create a demand. `pebble` is in `DEMAND_FREE_KINDS`, so the
// write gate refuses a clock on one; there is no route, no date, no return.
//
// The list shows what is ON you now. "Settled" takes the weight off: the log
// keeps both facts for ever, and the node joins everything else you have let go
// (ADR-0050), where it can be taken back. It has to leave what you are holding,
// because a settled pebble appears in no list at all — left in, it would count
// toward the gauge for ever with nothing on any screen to explain it.

import { MAGNITUDES, type Capacity, type Magnitude } from '../events.ts';
import { CAPACITY_WORDS, loadNow, pebbleWords } from '../load.ts';
import { ulid } from '../ids.ts';
import type { Session } from './session.ts';
import { declareCapacityEvents, raisePebbleEvents, settlePebbleEvents } from './load-intents.ts';
import { openSheet } from './sheets.ts';

/**
 * What the door says about what is behind it (2.8.1, ADR-0099).
 *
 * The entry is a surface now, and out of sight is the collision this whole app
 * is a rebuttal to — so the row that reaches it reports its own state rather
 * than going quiet. Your own word back to you, and how many weights you have
 * named; nothing else, and nothing inferred.
 *
 * EMPTY when there is nothing to report, because a door announcing "0" is a
 * standing reminder that you have not filled something in, which is the demand
 * this entry has been defended against since the day it was built.
 */
export function loadDoorWords(capacity: Capacity | null, pebbles: number): string {
  const said = capacity ? CAPACITY_WORDS[capacity] : null;
  const weights = pebbles === 0
    ? null
    : `${pebbles} ${pebbles === 1 ? 'thing' : 'things'} on you`;
  return [said, weights].filter(Boolean).join(' · ');
}

/** The same cleaner shape capture uses: a title is one line of text. */
const clean = (s: string): string => s.replace(/[ -]/g, ' ').trim().slice(0, 200);

export interface LoadUI {
  refresh(): void;
  /**
   * Open the entry with a piece of work already attached (1.24.0).
   *
   * `docs/nd-collisions.md` entry 2 — the wall of awful. Every avoidance
   * attached to a task adds a brick, until what stands between somebody and a
   * ten-minute chore is the history rather than the chore. The routing is to
   * let the person SAY a thing is heavy, and to route that through the load
   * machinery that already narrows the offer.
   *
   * `pebble.raised` has carried `affects` since 1.15.0, `raisePebbleEvents`
   * has accepted it since the day it was written, and `load.ts` already reads
   * the names out of it — and until now NO SURFACE HAS EVER SET IT. Eight
   * releases of a complete, unreachable field. This is its first writer.
   *
   * Explicitly NOT a detector. An inferred wall is the ledger this app exists
   * not to keep: weight is said or it does not exist.
   */
  attachTo(nodeId: string, title: string): void;
}

export function mountLoad(session: Session, onChange: () => void): LoadUI {
  const level = document.querySelector<HTMLSelectElement>('#capacity-level');
  const form = document.querySelector<HTMLFormElement>('#pebble-form');
  const text = document.querySelector<HTMLInputElement>('#pebble-text');
  const weight = document.querySelector<HTMLSelectElement>('#pebble-weight');
  const list = document.querySelector<HTMLUListElement>('#pebble-list');
  const entry = document.querySelector<HTMLDialogElement>('#sheet-load-entry');
  // Soft, like every optional handle here: a missing readout costs the door its
  // state line, never the entry itself.
  const doorCount = document.querySelector<HTMLElement>('#sheet-load-entry-count');
  if (!level || !form || !text || !weight || !list) {
    return { refresh() {}, attachTo() {} };
  }

  /**
   * What the next weight is about, if it was raised from a piece of work.
   *
   * CLEARED ON SUBMIT AND ON CLOSING THE ENTRY, both. A sticky attachment is
   * the defect this shape invites: raise one weight from the offer, come back
   * an hour later to name something unrelated, and the app quietly files it
   * against a task you have stopped thinking about. `affects` is a list a
   * person reads, so a wrong entry is not a wrong number — it is a false
   * sentence about their week.
   */
  //
  // `close`, not `toggle` (2.8.1): a dialog does not fire `toggle`, so the
  // listener that survived the move unchanged would have gone quiet and left
  // the attachment sticky for ever — the exact defect it was written to
  // prevent, with no symptom until somebody's weight was filed against last
  // week's task.
  let attached: string | null = null;
  entry?.addEventListener('close', () => { attached = null; });

  const paint = (): void => {
    const load = loadNow(session.state());

    // The door's own state, so the surface is not silent while it is shut.
    if (doorCount) {
      const words = loadDoorWords(load.capacity ?? null, load.pebbles.length);
      doorCount.textContent = words;
      doorCount.hidden = words === '';
    }

    // The select shows YOUR word back to you. Never overwritten while the
    // element has focus — the same no-clobber rule the detail sheet's name box
    // has, because a repaint landing mid-choice is how a surface throws away an
    // answer somebody was in the middle of giving.
    if (document.activeElement !== level) level.value = load.capacity ?? '';

    // Rows are built fresh each paint, and carry ONE verb — and it is not "do
    // this". A pebble is never work, and this list must never read as a to-do
    // list.
    list.replaceChildren(...load.pebbles.map((n) => {
      const li = document.createElement('li');
      const words = document.createElement('span');
      words.textContent = `${n.title || '(untitled)'} — ${pebbleWords(session.state(), n)}`;
      const off = document.createElement('button');
      off.type = 'button';
      off.className = 'ghost';
      // "Settled", not "Done". Nothing here was work, so nothing here is
      // completed — the weight came off, which is a different sentence.
      off.textContent = 'Settled';
      off.addEventListener('click', () => {
        void session.commit(ctx => settlePebbleEvents(ctx, n.id))
          .then(() => { paint(); onChange(); })
          .catch(() => { /* the surface repaints from state either way */ });
      });
      li.append(words, off);
      return li;
    }));
  };

  level.addEventListener('change', () => {
    const chosen = level.value as Capacity | '';
    if (!chosen || !(chosen in CAPACITY_WORDS)) return;
    void session.commit(ctx => declareCapacityEvents(ctx, chosen))
      .then(() => { paint(); onChange(); })
      .catch(() => { paint(); });
  });

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const title = clean(text.value);
    if (!title) { text.focus(); return; }
    const mag = weight.value as Magnitude;
    if (!(MAGNITUDES as readonly string[]).includes(mag)) return;
    const id = ulid(Date.parse(new Date().toISOString()));
    const affects = attached ? [attached] : [];
    void session.commit(ctx => raisePebbleEvents(ctx, id, title, mag, affects))
      .then(() => {
        // Cleared only after the write has LANDED (ADR-0008), like capture —
        // an input that empties before the commit resolves is an input that
        // can lose what you typed.
        text.value = '';
        // The attachment is spent. One weight, one thing it was about.
        attached = null;
        text.focus();
        paint();
        onChange();
      })
      .catch(() => { /* the text stays where it is, which is the point */ });
  });

  paint();
  return {
    refresh: paint,
    attachTo(nodeId, title) {
      attached = nodeId;
      // `openSheet`, so the one-surface-at-a-time rule applies here too — this
      // is reached FROM the offer's own detail sheet, and two stacked modals is
      // the overlap ADR-0083 forbids.
      openSheet('sheet-load-entry');
      // The placeholder names what this weight will be about, so the field is
      // not a blank box somebody has to remember the context for. The VALUE is
      // left empty on purpose: the weight is not the task's title, it is what
      // is heavy about it, and pre-filling would put words in somebody's mouth
      // on the one surface built for their own.
      text.placeholder = `What is heavy about “${title || 'this'}”?`;
      text.focus();
    },
  };
}
